import 'react-app-polyfill/stable';
import { DomEventType } from 'enums';

type MediaQueryListLegacyListener = (
  this: MediaQueryList,
  ev: MediaQueryListEvent
) => void;

const legacyListenerMap = new WeakMap<
  MediaQueryList,
  Map<EventListenerOrEventListenerObject, MediaQueryListLegacyListener>
>();

const polyfillObjectHasOwn = (): void => {
  if (typeof Object.hasOwn === 'function') {
    return;
  }

  Object.defineProperty(Object, 'hasOwn', {
    value: (obj: object, prop: PropertyKey): boolean =>
      Object.prototype.hasOwnProperty.call(obj, prop),
    configurable: true,
    writable: true,
    enumerable: false
  });
};

const getLegacyListenerMap = (
  mediaQueryList: MediaQueryList
): Map<EventListenerOrEventListenerObject, MediaQueryListLegacyListener> => {
  let listenerMap = legacyListenerMap.get(mediaQueryList);
  if (!listenerMap) {
    listenerMap = new Map();
    legacyListenerMap.set(mediaQueryList, listenerMap);
  }
  return listenerMap;
};

const removeLegacyListenerMapping = (
  mediaQueryList: MediaQueryList,
  listener: EventListenerOrEventListenerObject
): MediaQueryListLegacyListener | undefined => {
  const listenerMap = legacyListenerMap.get(mediaQueryList);
  if (!listenerMap) {
    return undefined;
  }

  const legacyListener = listenerMap.get(listener);
  if (!legacyListener) {
    return undefined;
  }

  listenerMap.delete(listener);
  if (listenerMap.size === 0) {
    legacyListenerMap.delete(mediaQueryList);
  }

  return legacyListener;
};

const wrapMediaQueryListener = (
  listener: EventListenerOrEventListenerObject
): MediaQueryListLegacyListener => {
  return function (
    this: MediaQueryList,
    change: MediaQueryListEvent
  ): void {
    const event = (change ?? this) as MediaQueryListEvent;
    if (typeof listener === 'function') {
      listener.call(this, event);
      return;
    }
    listener.handleEvent(event);
  };
};

const polyfillMediaQueryListListeners = (): void => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return;
  }

  const mediaQueryList = window.matchMedia('(max-width: 1px)');
  if (!mediaQueryList) {
    return;
  }

  const mediaQueryListPrototype = Object.getPrototypeOf(
    mediaQueryList
  ) as MediaQueryList;

  if (typeof mediaQueryListPrototype.addEventListener === 'function') {
    return;
  }

  mediaQueryListPrototype.addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject | null
  ): void {
    if (type !== DomEventType.CHANGE || listener === null) {
      return;
    }

    const legacyListener = wrapMediaQueryListener(listener);
    getLegacyListenerMap(this).set(listener, legacyListener);

    if (typeof this.addListener === 'function') {
      this.addListener(legacyListener);
    }
  };

  mediaQueryListPrototype.removeEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject | null
  ): void {
    if (type !== DomEventType.CHANGE || listener === null) {
      return;
    }

    const legacyListener = removeLegacyListenerMapping(this, listener);
    if (!legacyListener || typeof this.removeListener !== 'function') {
      return;
    }

    this.removeListener(legacyListener);
  };
};

polyfillObjectHasOwn();
polyfillMediaQueryListListeners();
